import { calcNights } from './bookingUtils';
import { normalizeTaxFeeBreakdown } from './taxFeeBreakdown';
import { getCustomerVisibleBaseAmount } from '../../utils/customerPricingVisibility';
import { normalizeBookingPricing } from '../../utils/bookingPricing';

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toText = (value) => (value ? String(value) : '');

const normalizeBookingIdValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') {
    const nested =
      value.id ||
      value.bookingId ||
      value.booking_id ||
      value.code ||
      value.bookingCode ||
      value.booking_code ||
      value.bookingRef ||
      value.booking_ref;
    if (nested === value) return '';
    return normalizeBookingIdValue(nested);
  }
  return '';
};

const resolveBookingId = (booking) =>
  normalizeBookingIdValue(
    booking?.id ||
      booking?.booking_id ||
      booking?.booking_code ||
      booking?.bookingCode ||
      booking?.bookingRef ||
      booking?.booking_ref ||
      booking?.bookingId
  );

const resolveProviderStayId = (booking) => {
  const providerCode = toText(booking?.provider_code || booking?.providerCode).toLowerCase();
  const providerPropertyId = toText(booking?.provider_property_id || booking?.providerPropertyId);
  if (!providerCode || !providerPropertyId) return '';
  return `provider:${providerCode}:${providerPropertyId}`;
};

const normalizeAddons = (booking) => {
  if (Array.isArray(booking?.addons)) {
    return booking.addons.map((addon) => ({
      id: toText(addon?.id || addon?.code || addon?.name),
      name: addon?.name || addon?.title || addon?.id || 'Addon',
      price: toNumber(addon?.price ?? addon?.amount),
    }));
  }
  const extras = booking?.pricing_snapshot_json?.extras || booking?.totals_json?.extras || booking?.extras_json || [];
  if (!Array.isArray(extras)) return [];
  return extras
    .map((extra) => ({
      id: toText(extra?.id || extra?.code || extra?.name).toLowerCase().replace(/\s+/g, '_'),
      name: extra?.name || extra?.title || 'Addon',
      price: toNumber(extra?.amount ?? extra?.price),
    }))
    .filter((addon) => addon.id);
};

const normalizePricing = (booking, addons) => {
  const snapshot = booking?.pricing_snapshot_json || booking?.totals_json || {};
  const pricingSource = {
    ...snapshot,
    ...(booking?.pricing || {}),
    currency: booking?.pricing?.currency || snapshot.currency || booking.currency || 'INR',
    nights:
      booking?.pricing?.nights ||
      snapshot.nights ||
      booking?.nights ||
      calcNights(booking?.check_in_date, booking?.check_out_date),
  };
  const normalized = normalizeBookingPricing(pricingSource, {
    nights: pricingSource.nights,
    currency: pricingSource.currency,
  });
  const extrasTotal = toNumber(snapshot.extrasTotal);
  const addonsTotal =
    snapshot.base_price !== undefined || snapshot.taxable_subtotal !== undefined
      ? 0
      : extrasTotal || addons.reduce((sum, addon) => sum + toNumber(addon.price), 0);
  return {
    base: normalized.basePrice || getCustomerVisibleBaseAmount(
      snapshot,
      toNumber(snapshot.base_nights_amount ?? snapshot.roomSubtotal ?? snapshot.totals?.roomSubtotal)
    ),
    basePrice: normalized.basePrice,
    addons: addonsTotal,
    discount: normalized.discountAmount,
    discountAmount: normalized.discountAmount,
    taxableSubtotal: normalized.taxableSubtotal,
    tax: normalized.gstAmount,
    gstAmount: normalized.gstAmount,
    fees: normalized.mandatoryFees,
    mandatoryFees: normalized.mandatoryFees,
    total: normalized.finalPayable,
    finalPayable: normalized.finalPayable,
    currency: normalized.currency || snapshot.currency || booking.currency || 'INR',
  };
};

const normalizeStatus = (booking) => {
  const explicitStatus = String(booking?.status || '').toUpperCase();
  if (explicitStatus) return explicitStatus;
  const paymentStatus = String(booking?.payment_status || '').toUpperCase();
  if (paymentStatus === 'PARTIAL') return 'PARTIALLY_PAID';
  if (paymentStatus === 'PAID' || paymentStatus === 'CAPTURED') return 'PAID';
  if (paymentStatus === 'FAILED') return 'FAILED';
  const bookingStatus = String(booking?.booking_status || '').toUpperCase();
  if (bookingStatus === 'CONFIRMED') return 'PAID';
  if (bookingStatus === 'PENDING' || bookingStatus === 'DRAFT') return 'DRAFT';
  return bookingStatus || 'DRAFT';
};

export const normalizeBookingDraft = (booking, stayData) => {
  if (!booking) return null;
  const stayName =
    booking.stayName ||
    booking.stay_name ||
    booking.property_name ||
    stayData?.title ||
    stayData?.name ||
    '';
  const location =
    booking.location ||
    booking.property_location ||
    stayData?.location ||
    [stayData?.city, stayData?.area].filter(Boolean).join(', ') ||
    '';
  const addons = normalizeAddons(booking);
  const pricing = normalizePricing(booking, addons);
  const checkIn = booking.checkIn || booking.check_in || booking.check_in_date || '';
  const checkOut = booking.checkOut || booking.check_out || booking.check_out_date || '';
  const nights = toNumber(booking.nights || pricing?.nights || booking.pricing?.nights) || calcNights(checkIn, checkOut);
  const guests = toNumber(booking.guests || booking.no_of_guests || booking.guests_count) || 0;
  const roomType =
    booking.room_type ||
    booking.roomType ||
    booking.pricing_snapshot_json?.room_type ||
    booking.totals_json?.room_type ||
    null;
  const roomTypeId =
    booking.room_type_id ||
    booking.roomTypeId ||
    roomType?.id ||
    booking.room_type?.id ||
    null;
  const roomCount = toNumber(booking.room_count || booking.roomCount || 1) || 1;
  const appliedCardOffer =
    booking.applied_card_offer ||
    booking.pricing_snapshot_json?.applied_card_offer ||
    booking.totals_json?.applied_card_offer ||
    null;
  const eligibleCardOffers =
    booking.eligible_card_offers ||
    booking.pricing_snapshot_json?.eligible_card_offers ||
    booking.totals_json?.eligible_card_offers ||
    booking.pricing_snapshot_json?.breakdown?.card_offers ||
    [];
  const cardOfferId = toText(
    booking.cardOfferId ||
    booking.card_offer_id ||
    appliedCardOffer?.id ||
    ''
  );
  const guestDetails =
    booking.guestDetails ||
    booking.guest_details || {
      fullName: booking.customer_name || booking.guest_name || '',
      email: booking.customer_email || booking.guest_email || '',
      phone: booking.customer_phone || booking.guest_phone || '',
      arrivalTime: booking.arrival_time || '',
      specialRequests: booking.special_requests || '',
    };
  const paymentOptions = booking.payment_options || booking.pricing_snapshot_json?.payment_options || null;
  const paymentOptionSelected = String(
    booking.payment_mode ||
      booking.payment_option_selected ||
      booking.partial_payment_snapshot?.payment_option_selected ||
      'full'
  ).toLowerCase() === 'partial'
    ? 'partial'
    : 'full';
  const partialPaymentPercentage = toNumber(
    booking.partial_percentage ||
      booking.partial_payment_percentage ||
      booking.partial_payment_snapshot?.partial_payment_percentage
  ) || null;
  const amountPaidNow = toNumber(
    booking.partial_paid_amount ||
      booking.amount_paid_now ||
      booking.partial_payment_snapshot?.amount_paid_now ||
      (paymentOptionSelected === 'full' ? pricing.total : 0)
  );
  const remainingAmount = toNumber(
    booking.remaining_amount ||
      booking.partial_payment_snapshot?.remaining_amount
  );
  const paymentStatusExtended =
    booking.payment_status_extended ||
    booking.partial_payment_snapshot?.payment_status_extended ||
    '';
  const taxFeeBreakdown = normalizeTaxFeeBreakdown(
    booking.tax_fee_breakdown ||
      booking.pricing_snapshot_json?.tax_fee_breakdown ||
      booking.pricing_snapshot_json?.taxes_and_fees ||
      null,
    {
      fee_total: booking.fee_total ?? pricing.fees,
      tax_total: booking.tax_total ?? pricing.tax,
    }
  );

  return {
    id: resolveBookingId(booking),
    stayId: booking.stayId || booking.stay_id || booking.property_id || booking.propertyId || resolveProviderStayId(booking),
    stayName,
    location,
    stay: stayData || null,
    checkIn,
    checkOut,
    nights,
    guests,
    roomTypeId,
    roomCount,
    roomType,
    addons,
    coupon: booking.coupon || booking.pricing_snapshot_json?.coupon || (booking.coupon_code ? { code: booking.coupon_code } : null),
    cardOfferId,
    eligibleCardOffers: Array.isArray(eligibleCardOffers) ? eligibleCardOffers : [],
    appliedCardOffer,
    pricing,
    paymentOptions,
    paymentOptionSelected,
    paymentMode: booking.payment_mode || paymentOptionSelected,
    partialPaymentPercentage,
    partialPercentage: partialPaymentPercentage,
    amountPaidNow,
    partialPaidAmount: paymentOptionSelected === 'partial' ? amountPaidNow : null,
    remainingAmount,
    paymentStatusExtended,
    feeTotal: booking.fee_total ?? taxFeeBreakdown.feeTotal ?? pricing.fees,
    taxTotal: booking.tax_total ?? taxFeeBreakdown.taxTotal ?? pricing.tax,
    taxFeeBreakdown,
    guestDetails,
    status: normalizeStatus(booking),
    createdAt: booking.createdAt || booking.created_at || '',
    updatedAt: booking.updatedAt || booking.updated_at || '',
  };
};
